import resourceTypesData from './resourceTypes.json';

/**
 * Resource Types Utility Functions
 *
 * Provides helper functions to access resource type hierarchy data.
 * These functions act as "fake local APIs" that can be easily replaced
 * with real API calls later if needed.
 */

export interface ActivityType {
  name: string;
  activityTypeId: number;
}

export interface ResourceType {
  resourceTypeId: number;
  activityTypes: ActivityType[];
}

export type ResourceTypeName =
  | 'Guide'
  | 'Template'
  | 'Video'
  | 'Blog'
  | 'Webinar'
  | 'Playlist';

/**
 * Get all resource types with their hierarchies
 */
export const getAllResourceTypes = (): Record<
  ResourceTypeName,
  ResourceType
> => {
  return resourceTypesData as Record<ResourceTypeName, ResourceType>;
};

/**
 * Get a specific resource type by name
 */
export const getResourceType = (
  name: ResourceTypeName
): ResourceType | undefined => {
  return resourceTypesData[name];
};

/**
 * Get resource type ID by resource type name
 */
export const getResourceTypeId = (
  name: ResourceTypeName
): number | undefined => {
  return resourceTypesData[name]?.resourceTypeId;
};

/**
 * Get all activity types for a specific resource type
 */
export const getActivityTypesForResourceType = (
  resourceTypeName: ResourceTypeName
): ActivityType[] => {
  return resourceTypesData[resourceTypeName]?.activityTypes || [];
};

/**
 * Get activity type ID by resource type and activity type name
 */
export const getActivityTypeId = (
  resourceTypeName: ResourceTypeName,
  activityTypeName: string
): number | undefined => {
  const activityTypes = getActivityTypesForResourceType(resourceTypeName);
  return activityTypes.find(at => at.name === activityTypeName)?.activityTypeId;
};

/**
 * Get activity type name by resource type and activity type ID
 */
export const getActivityTypeName = (
  resourceTypeName: ResourceTypeName,
  activityTypeId: number
): string | undefined => {
  const activityTypes = getActivityTypesForResourceType(resourceTypeName);
  return activityTypes.find(at => at.activityTypeId === activityTypeId)?.name;
};

/**
 * Get all resource type names (for dropdowns/filters)
 */
export const getResourceTypeNames = (): ResourceTypeName[] => {
  return Object.keys(resourceTypesData) as ResourceTypeName[];
};

/**
 * Get resource type name by resource type ID
 */
export const getResourceTypeNameById = (
  resourceTypeId: number
): ResourceTypeName | undefined => {
  const entries = Object.entries(resourceTypesData) as [
    ResourceTypeName,
    ResourceType
  ][];
  const found = entries.find(
    ([, type]) => type.resourceTypeId === resourceTypeId
  );
  return found?.[0];
};

/**
 * Convert resource type name to lowercase (for route query params)
 */
export const resourceTypeToRouteParam = (
  resourceType: ResourceTypeName
): string => {
  return resourceType.toLowerCase();
};

/**
 * Convert route query param to resource type name
 */
export const routeParamToResourceType = (
  param: string
): ResourceTypeName | undefined => {
  const normalized =
    param.charAt(0).toUpperCase() + param.slice(1).toLowerCase();
  return getResourceTypeNames().find(name => name === normalized);
};

/**
 * Check if an activity type is valid for a given resource type
 */
export const isValidActivityTypeForResourceType = (
  resourceTypeName: ResourceTypeName,
  activityTypeName: string
): boolean => {
  const activityTypes = getActivityTypesForResourceType(resourceTypeName);
  return activityTypes.some(at => at.name === activityTypeName);
};

/**
 * Get all unique activity type names across all resource types
 */
export const getAllActivityTypeNames = (): string[] => {
  const allActivityTypes = new Set<string>();
  Object.values(resourceTypesData).forEach(resourceType => {
    resourceType.activityTypes.forEach(at => {
      allActivityTypes.add(at.name);
    });
  });
  return Array.from(allActivityTypes).sort();
};
